import { Injectable, signal } from '@angular/core';
import { Keyword } from '../class/Keyword';

export type KeywordDictionary = Record<number, Keyword>;

/**
 * Holds the keyword dictionary shared between KeywordSetService (owns the
 * business logic: creation, association) and GlobalSearchService (reads it to
 * index every entity by the labels of its attached keywords), without either
 * service depending on the other for it.
 *
 * The signal is read-only from the outside - all writes must go through
 * setKeywords/registerKeyword/clear so every change to the shared state is
 * traceable to one of these methods.
 */
@Injectable({
  providedIn: 'root',
})
export class KeywordStateService {
  private _keywords = signal<KeywordDictionary>({});

  public $keywords = this._keywords.asReadonly();

  public setKeywords(keywords: KeywordDictionary): void {
    this._keywords.set(keywords ?? {});
  }

  public registerKeyword(id: number, label: string): void {
    // A signal only notifies readers on a new reference: mutating the current
    // dictionary in place (e.g. `this.$keywords()[id] = ...`) would silently
    // skip every subscriber, templates included.
    this._keywords.update(current => ({ ...current, [id]: new Keyword(id, undefined, label) }));
  }

  public clear(): void {
    this._keywords.set({});
  }
}
